/**
 * QuickDepartureDialog — Minimal confirmation for scheduled departures.
 *
 * When a departure already has a schedule record (scheduleId exists),
 * we just mark it completed + record actual_at + fire departure event.
 * No form fields needed — all info is already in the schedule.
 */

import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog'
import { useUpdateAircraftSchedule } from '@/hooks/useAircraftSchedules'
import { useCreateDepartureEvent } from '@/hooks/useAllocationEvents'
import type { ScheduleItem } from './useTodayBoardData'

interface QuickDepartureDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: ScheduleItem
  locationId: string
}

function getCurrentTimeDisplay(): string {
  const now = new Date()
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
}

export function QuickDepartureDialog({
  open,
  onOpenChange,
  item,
  locationId,
}: QuickDepartureDialogProps) {
  const updateSchedule = useUpdateAircraftSchedule()
  const departureEvent = useCreateDepartureEvent()
  const isPending = updateSchedule.isPending || departureEvent.isPending

  const tailNumber = item.data.aircraft.tailNumber
  const destination = item.airportIcao
    ? `${item.airportIcao}${item.airportName ? ` (${item.airportName})` : ''}`
    : 'unknown destination'

  const handleConfirm = async () => {
    try {
      const now = new Date().toISOString()

      // 1. Update schedule to completed with actual_at
      if (item.scheduleId) {
        await updateSchedule.mutateAsync({
          id: item.scheduleId,
          status: 'completed',
          actual_at: now,
        })
      }

      // 2. Fire departure event (if allocation exists)
      if (item.allocationId) {
        await departureEvent.mutateAsync({
          allocation_id: item.allocationId,
          aircraft_id: item.data.aircraft.id,
          location_id: locationId,
          destination_icao: item.airportIcao || null,
          destination_name: item.airportName || null,
        })
      }

      toast.success(`${tailNumber} marked as departed`)
      onOpenChange(false)
    } catch (err) {
      toast.error('Failed to record departure')
      console.error('Quick departure error:', err)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Departure</AlertDialogTitle>
          <AlertDialogDescription>
            Confirm that <strong>{tailNumber}</strong> has departed to{' '}
            <strong>{destination}</strong> at {getCurrentTimeDisplay()}?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={isPending}>
            {isPending ? 'Recording...' : 'Confirm Departure'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
